(function () {
  'use strict';

  // Single owner of the `location_catalog_json-<locationId>` localStorage
  // shape: what gets stored, how many locations are retained, and how a write
  // recovers from a full quota.
  //
  // Why this exists: the API returns the whole catalog (~552 KB for a typical
  // location — every price entry carries the product title and a CDN image URL
  // that nothing here reads). Storing that verbatim overflows the ~5 MB
  // localStorage quota after ~9 locations. The write was wrapped in a bare
  // `try {} catch (e) {}`, so the failure was silent, the cache never
  // persisted, and every page load re-fetched the catalog — paying the
  // ~3s cold-cache cost on the API each time.
  //
  // Two fixes: store only the fields the readers actually consume (4.2x
  // smaller), and retain few enough locations that the quota is never the
  // binding constraint.

  const KEY_PREFIX = 'location_catalog_json';

  // Readers only ever need the current location; one spare keeps a
  // back-and-forth between two locations warm. Deliberately far below the
  // quota rather than as close to it as we can get.
  const MAX_CACHED_LOCATIONS = 2;

  function keyFor(locationId) {
    return KEY_PREFIX + '-' + String(locationId);
  }

  function catalogKeys() {
    try {
      // Match `<prefix>-<locationId>` exactly as keyFor() builds it. A looser
      // prefix test would also match a bare `location_catalog_json` key, which
      // this module does not own and the quota-recovery path would delete.
      const keyPrefix = KEY_PREFIX + '-';
      return Object.keys(localStorage).filter(function (key) {
        return key.indexOf(keyPrefix) === 0;
      });
    } catch (e) {
      // Blocked storage (Safari private mode, "block all cookies") throws on
      // enumeration, not just on access.
      return [];
    }
  }

  // captureMessage's second argument is a Sentry *level* string, so extras
  // handed to it are dropped on the floor. captureException is the facade
  // method that accepts { level, extra }, so the diagnostics actually arrive.
  function report(message, extra) {
    try {
      if (window.etObservability && typeof window.etObservability.captureException === 'function') {
        window.etObservability.captureException(new Error(message), {
          level: 'warning',
          extra: extra
        });
      }
    } catch (e) {}
  }

  // Strip `gid://shopify/ProductVariant/123` down to `123`. Every reader runs
  // its own gid-normalizing helper over these values, so storing the bare id
  // is equivalent to storing the gid — and materially smaller.
  function normalizeEntityId(value) {
    const raw = (value == null ? '' : String(value)).trim();
    if (!raw) return '';
    if (raw.indexOf('gid://') !== -1) {
      const parts = raw.split('/');
      return parts[parts.length - 1] || raw;
    }
    return raw;
  }

  // Field precedence mirrors location-catalog-pricing.js's extractAmount
  // exactly: whatever that reader would have resolved from the verbatim entry
  // has to be what the slimmed entry carries, or the cached price silently
  // differs from the uncached one.
  function extractAmount(entry) {
    if (!entry) return null;
    if (entry.amount != null) return entry.amount;
    if (entry.value != null) return entry.value;
    if (entry.price != null) {
      if (typeof entry.price === 'number' || typeof entry.price === 'string') return entry.price;
      if (entry.price.amount != null) return entry.price.amount;
      if (entry.price.value != null) return entry.price.value;
    }
    if (entry.unit_price != null) return entry.unit_price;
    if (entry.unitPrice != null) return entry.unitPrice;
    return null;
  }

  function extractVariantId(entry) {
    if (!entry) return '';
    return normalizeEntityId(
      entry.variant_id ||
      entry.variantId ||
      entry.variantGid ||
      (entry.variant && (entry.variant.id || entry.variant.variantId || entry.variant.variant_id)) ||
      (entry.merchandise && entry.merchandise.id) ||
      entry.variant
    );
  }

  function extractCatalogId(entry) {
    if (!entry) return '';
    return normalizeEntityId(
      entry.catalog_id ||
      entry.catalogId ||
      (entry.catalog && (entry.catalog.id || entry.catalog.catalogId)) ||
      // `catalog` as a bare id — the same last resort
      // location-catalog-pricing.js's extractCatalogId takes. Dropping it here
      // would strip the catalog id out of the cache, and applyCatalogPricing
      // then hides the card as "no price". Numbers count: the reader takes
      // `entry.catalog` untyped, so a numeric id resolves there and must
      // survive the projection too.
      ((typeof entry.catalog === 'string' || typeof entry.catalog === 'number') ? entry.catalog : '')
    );
  }

  // Project each price entry down to the fields the three readers consume
  // (location-catalog-pricing.js, snippets/cart-drawer.liquid,
  // sections/related-products.liquid): a price amount, a variant id, and a
  // catalog id when the payload carries one. The output keeps the same nested
  // shape those readers already destructure, so none of them need to change.
  function slimCatalog(catalog) {
    if (!catalog) return catalog;

    const rawPrices = catalog.prices;
    if (!Array.isArray(rawPrices)) return catalog;

    const prices = [];
    for (let i = 0; i < rawPrices.length; i++) {
      const entry = rawPrices[i];
      const amount = extractAmount(entry);
      if (amount == null) continue;

      const slim = { price: { amount: String(amount) } };
      const variantId = extractVariantId(entry);
      if (variantId) slim.variant = { id: variantId };
      const catalogId = extractCatalogId(entry);
      if (catalogId) slim.catalog_id = catalogId;

      prices.push(slim);
    }

    // catalogId/catalogTitle are not read back by anything — they are kept
    // because they cost ~60 bytes against a 130 KB entry and make a dumped
    // cache entry identifiable when debugging a "wrong prices" report, which
    // is otherwise 2000 anonymous id/amount pairs.
    return {
      catalogId: catalog.catalogId,
      catalogTitle: catalog.catalogTitle,
      prices: prices
    };
  }

  // Drop cached catalogs for other locations, oldest first, keeping `keep`
  // entries plus whatever `protectLocationId` names.
  function trim(protectLocationId, keep) {
    const limit = typeof keep === 'number' ? keep : MAX_CACHED_LOCATIONS;
    const protectedKey = protectLocationId ? keyFor(protectLocationId) : null;
    const keys = catalogKeys().filter(function (key) { return key !== protectedKey; });
    if (keys.length === 0) return 0;

    const budget = protectedKey ? Math.max(0, limit - 1) : limit;
    if (keys.length <= budget) return 0;

    const entries = keys.map(function (key) {
      let ts = 0;
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || 'null');
        const raw = parsed && (parsed.timestamp != null ? parsed.timestamp : parsed.updatedAt);
        const numeric = Number(raw);
        ts = isNaN(numeric) ? 0 : numeric;
      } catch (e) {}
      return { key: key, ts: ts };
    });

    entries.sort(function (a, b) { return a.ts - b.ts; });

    let removed = 0;
    for (let i = 0; i < entries.length - budget; i++) {
      try {
        localStorage.removeItem(entries[i].key);
        removed++;
      } catch (e) {}
    }
    return removed;
  }

  function isQuotaError(error) {
    if (!error) return false;
    // Chromium: QuotaExceededError. Safari: QUOTA_EXCEEDED_ERR / code 22.
    // Firefox: NS_ERROR_DOM_QUOTA_REACHED (code 1014).
    return error.name === 'QuotaExceededError' ||
      error.name === 'QUOTA_EXCEEDED_ERR' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      error.code === 22 ||
      error.code === 1014;
  }

  // True when localStorage will accept a write at all. Distinguishes "the
  // origin's budget is full" (actionable) from "this browser gives the origin
  // a zero-byte quota" — Safari private browsing throws QuotaExceededError on
  // every setItem, and without this check each catalog write would raise an
  // identical, unactionable Sentry event.
  function storageAcceptsWrites() {
    // Deliberately not under KEY_PREFIX, so a probe that somehow outlives this
    // call can never be mistaken for a cached catalog by catalogKeys().
    const probeKey = 'et_catalog_cache_probe';
    try {
      localStorage.setItem(probeKey, '1');
      localStorage.removeItem(probeKey);
      return true;
    } catch (e) {
      try { localStorage.removeItem(probeKey); } catch (e2) {}
      return false;
    }
  }

  // Reported at most once per page view — see the latch in write().
  let hasReportedQuotaFailure = false;

  // A setItem that neither threw nor stored anything is a real case (some
  // Safari builds under blocked storage), so confirm the key reads back.
  // Presence is enough — comparing the whole payload would mean holding a
  // second copy of a 130 KB string for no extra signal.
  function storedValueMatches(key) {
    try {
      return localStorage.getItem(key) != null;
    } catch (e) {
      return false;
    }
  }

  // Store the catalog for a location. Returns true when the value was written
  // and reads back; some Safari builds accept a setItem and then hand back
  // null, and a caller that trusted a bare "didn't throw" would believe a
  // catalog was cached when nothing was. A quota failure is recovered from by
  // evicting other cached catalogs and retrying, rather than being swallowed —
  // a silent failure here is what made every page load re-fetch.
  function write(locationId, locationName, catalog) {
    if (!locationId || !catalog) return false;

    const key = keyFor(locationId);
    const payload = JSON.stringify({
      data: { catalog: slimCatalog(catalog) },
      timestamp: Date.now(),
      locationId: String(locationId),
      locationName: String(locationName || '')
    });

    trim(locationId);

    // A quota-exceeded setItem leaves the existing value in place, so the
    // recovery below is what would otherwise destroy it. Keep a copy: giving
    // up on a fresh catalog is cheap, throwing away a working one costs the
    // shopper a re-fetch on every page load.
    let previous = null;
    try {
      previous = localStorage.getItem(key);
    } catch (e) {}

    try {
      localStorage.setItem(key, payload);
      if (storedValueMatches(key)) return true;
    } catch (error) {
      if (!isQuotaError(error)) {
        // Blocked storage — nothing to recover, and not worth reporting on
        // every write.
        return false;
      }
    }

    // Quota hit even after trimming: something else on the origin is using the
    // budget. Drop every cached catalog (including any older copy of this one)
    // and try once more.
    try {
      catalogKeys().forEach(function (existing) {
        try { localStorage.removeItem(existing); } catch (e) {}
      });
      localStorage.setItem(key, payload);
      if (storedValueMatches(key)) return true;
    } catch (e) {}

    // Recovery failed: the retry threw, or it was accepted and did not read
    // back. Probe before restoring — every cached catalog has just been
    // dropped, so a probe that still fails means the budget is gone for
    // reasons this module cannot influence (a zero-quota browser), not that
    // the origin is merely full. Restoring first would shrink the budget and
    // mask the difference.
    const worthReporting = storageAcceptsWrites();
    if (previous != null) {
      try { localStorage.setItem(key, previous); } catch (e) {}
    }
    // Latched: the probe only proves a single byte fits, which it does even at
    // 99.9% full. When something else on the origin owns the budget every
    // catalog write fails the same way, so without this the shopper emits an
    // identical event on every write, forever. Once per page view is enough to
    // see the condition.
    if (worthReporting && !hasReportedQuotaFailure) {
      hasReportedQuotaFailure = true;
      report('Catalog cache write failed: storage full or write not durable', {
        locationId: String(locationId),
        payloadBytes: payload.length
      });
    }
    return false;
  }

  // Read the cached catalog for one location. Reading only the selected key
  // avoids parsing every cached catalog on each pricing refresh.
  function read(locationId) {
    if (!locationId) return null;
    try {
      const raw = localStorage.getItem(keyFor(locationId));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  window.EtCatalogCache = {
    KEY_PREFIX: KEY_PREFIX,
    MAX_CACHED_LOCATIONS: MAX_CACHED_LOCATIONS,
    keyFor: keyFor,
    catalogKeys: catalogKeys,
    slimCatalog: slimCatalog,
    trim: trim,
    write: write,
    read: read
  };
})();
